import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  Calendar,
  ExternalLink,
  FileText,
  Home,
  Link2,
  Loader2,
  Plus,
  Settings,
  Tag,
  X,
} from 'lucide-react';
import { dataroomApi } from '../../../api/dataroomApi';
import { dataSourcesApi } from '../../../api/dataSourcesApi';
import { resultsApi } from '../../../api/resultsApi';
import { topicsApi } from '../../../api/topicsApi';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import { formatModeratorApiKeyError, getModeratorApiKey } from '../../../utils/apiClient';
import {
  openImportDocument,
  parseImportDocumentId,
} from '../../../utils/importDocumentDownload';
import { addArticleToDataroom, dedupeStrings } from '../../state/dataroomStore';
import type { ResultFromAgentWithNames, RawDataWithTopicsRow } from '../../../types/models/api.results';
import type { SubscriptionTopic } from '../../../types/api';
import { TopicSearchPicker } from './TopicMultiSelect';
import './ArticleAnalysisPage.css';

type ArticleAnalysisLocationState = {
  articleId?: string | number;
  sourceUrl?: string;
  scrapedDate?: string;
} | null;

type TagTone = 'blue' | 'amber' | 'green' | 'violet' | 'slate';

const TAG_TONES: TagTone[] = ['blue', 'amber', 'green', 'violet', 'slate'];

function toneForIndex(i: number): TagTone {
  return TAG_TONES[i % TAG_TONES.length];
}

function formatTimeAgo(
  iso: string | undefined,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (!iso) return t('common.unknownTime');
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return t('common.unknownTime');
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return t('common.secondsAgo', { n: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('common.minutesAgoShort', { n: min });
  const h = Math.floor(min / 60);
  if (h < 48) return t('common.hoursAgoShort', { n: h });
  const d = Math.floor(h / 24);
  return t('common.daysAgoShort', { n: d });
}

function formatMetaDateTime(iso: string | undefined, localeTag: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const time = d.toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString(localeTag, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${time} · ${date}`;
}

function articleLink(raw: RawDataWithTopicsRow, sourceUrlFromNav?: string): string {
  const u = raw.url ?? raw.link ?? sourceUrlFromNav ?? '';
  if (!u) return '';
  if (u.startsWith('import-document://')) return u;
  return u.startsWith('http') ? u : `https://${u}`;
}

function linkDisplayLabel(href: string, title?: string): string {
  const docId = parseImportDocumentId(href);
  if (docId != null) {
    return title?.trim() ? `${title.trim()} (document)` : `Imported document #${docId}`;
  }
  return href.replace(/^https?:\/\//, '');
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0] + parts[parts.length - 1]![0]).toUpperCase().slice(0, 2);
  }
  return name.slice(0, 2).toUpperCase() || '?';
}

const CO_LOGO_BG = [
  'linear-gradient(135deg,#2563eb,#6366f1)',
  'linear-gradient(135deg,#f59e0b,#ef4444)',
  'linear-gradient(135deg,#16a34a,#0ea5e9)',
  'linear-gradient(135deg,#7c3aed,#ec4899)',
  'linear-gradient(135deg,#0ea5e9,#22d3ee)',
];

function parseCompanyField(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function dedupeLabels(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const label = value.trim();
    if (!label) continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

function pickResultForArticle(
  results: ResultFromAgentWithNames[],
  rawId: number,
): ResultFromAgentWithNames | null {
  const matches = results.filter((r) => r.rawDataId === rawId);
  if (matches.length === 0) return null;
  const sorted = [...matches].sort((a, b) => {
    const tb = new Date(b.updated_at ?? b.created_at).getTime();
    const ta = new Date(a.updated_at ?? a.created_at).getTime();
    return tb - ta;
  });
  return sorted[0] ?? null;
}

function looksLikeCodeSummary(summary: string): boolean {
  const compact = summary.replace(/\s+/g, ' ').trim();
  if (!compact) return false;
  const lower = compact.toLowerCase();
  const markers = [
    '<script',
    'function(',
    'function ',
    'var ',
    'const ',
    'let ',
    'window.',
    'document.',
    'datalayer',
    'gtm.js',
    'getelementsbytagname',
    'createelement(',
  ];
  if (markers.some((marker) => lower.includes(marker))) return true;
  const codeSignals = [
    /\bfunction\b/,
    /\b(var|const|let)\b/,
    /=>/,
    /[{};]{2,}/,
    /\w+\([^)]*\)/,
  ].filter((pattern) => pattern.test(compact)).length;
  return codeSignals >= 2;
}

function sanitizeSummary(summary: string | null | undefined): string {
  const cleaned = summary?.trim() ?? '';
  if (!cleaned || looksLikeCodeSummary(cleaned)) return '';
  return cleaned;
}

function summaryParagraphs(summary: string | null | undefined): string[] {
  const cleaned = sanitizeSummary(summary);
  if (!cleaned) return [];
  const parts = cleaned.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (parts.length > 0) return parts;
  return [cleaned];
}

function isAbortErr(e: unknown): boolean {
  return axios.isAxiosError(e) && (e.code === 'ERR_CANCELED' || (e as { name?: string }).name === 'CanceledError');
}

type ToastApi = {
  showSuccess: (msg: string, duration?: number) => void;
  showError: (msg: string, duration?: number) => void;
};

export function ArticleAnalysisPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast() as ToastApi;
  const { t, localeTag } = useUiLocale();
  const state = location.state as ArticleAnalysisLocationState;

  const articleIdNum = useMemo(() => {
    const raw = state?.articleId;
    if (raw === undefined || raw === null || raw === '') return NaN;
    return typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10);
  }, [state?.articleId]);

  const scrapedDate = state?.scrapedDate?.trim() || undefined;
  const sourceUrlFromNav = state?.sourceUrl;

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rawArticle, setRawArticle] = useState<RawDataWithTopicsRow | null>(null);
  const [analysis, setAnalysis] = useState<ResultFromAgentWithNames | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addingTag, setAddingTag] = useState(false);
  const [catalogTopics, setCatalogTopics] = useState<SubscriptionTopic[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [removingTopicId, setRemovingTopicId] = useState<number | null>(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [companyDraft, setCompanyDraft] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  const toastTimer = useRef<number | null>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!addingTag) return;
    const ac = new AbortController();
    setLoadingTopics(true);
    (async () => {
      try {
        const res = await topicsApi.listCatalog({ signal: ac.signal });
        if (ac.signal.aborted) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setCatalogTopics(
          rows.map((row) => ({
            id: row.id,
            topic: row.topic,
            color: row.color ?? null,
          })),
        );
      } catch (e: unknown) {
        if (ac.signal.aborted || isAbortErr(e)) return;
        setCatalogTopics([]);
        showError(formatModeratorApiKeyError(e));
        setAddingTag(false);
      } finally {
        if (!ac.signal.aborted) setLoadingTopics(false);
      }
    })();
    return () => ac.abort();
  }, [addingTag, showError]);

  useEffect(() => {
    if (addingCompany) companyInputRef.current?.focus();
  }, [addingCompany]);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    if (!state?.articleId || Number.isNaN(articleIdNum)) {
      setLoading(false);
      setRawArticle(null);
      setAnalysis(null);
      setFetchError(null);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setFetchError(null);

    (async () => {
      try {
        const listParams: Record<string, string | number> = { limit: 500 };
        if (scrapedDate) listParams.date = scrapedDate;

        const resultsParams: Record<string, string | number> = { limit: 1000 };
        if (scrapedDate) resultsParams.date = scrapedDate;

        const loadResultsSafe = async (params: Record<string, string | number>): Promise<ResultFromAgentWithNames[]> => {
          try {
            const res = await resultsApi.list(params, { signal: ac.signal });
            if (ac.signal.aborted) return [];
            return Array.isArray(res.data) ? res.data : [];
          } catch (e: unknown) {
            if (ac.signal.aborted || isAbortErr(e)) return [];
            return [];
          }
        };

        let dsRes;
        try {
          dsRes = await dataSourcesApi.list(listParams, { signal: ac.signal });
        } catch (e: unknown) {
          if (ac.signal.aborted || isAbortErr(e)) return;
          throw e;
        }
        if (ac.signal.aborted) return;

        let raw: RawDataWithTopicsRow | undefined = (Array.isArray(dsRes.data) ? dsRes.data : []).find(
          (r: RawDataWithTopicsRow) => r.id === articleIdNum,
        );

        if (!raw) {
          try {
            const wider = await dataSourcesApi.list({ limit: 1000 }, { signal: ac.signal });
            if (ac.signal.aborted) return;
            raw = (Array.isArray(wider.data) ? wider.data : []).find((r: RawDataWithTopicsRow) => r.id === articleIdNum);
          } catch (e: unknown) {
            if (ac.signal.aborted || isAbortErr(e)) return;
            throw e;
          }
        }

        if (!raw) {
          setRawArticle(null);
          setAnalysis(null);
          setFetchError('not-found');
          return;
        }

        let results = await loadResultsSafe(resultsParams);
        let match = pickResultForArticle(results, articleIdNum);

        if (!match && scrapedDate) {
          const all = await loadResultsSafe({ limit: 1000 });
          match = pickResultForArticle(all, articleIdNum);
        }

        if (ac.signal.aborted) return;
        setRawArticle(raw);
        setAnalysis(match);
        setFetchError(null);
      } catch (e: unknown) {
        if (ac.signal.aborted || isAbortErr(e)) return;
        setFetchError('network');
        setRawArticle(null);
        setAnalysis(null);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [articleIdNum, scrapedDate, state?.articleId]);

  const href = rawArticle ? articleLink(rawArticle, sourceUrlFromNav) : '';
  const importDocId = href ? parseImportDocumentId(href) : null;

  const handleArticleLinkClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    if (importDocId == null) return;
    e.preventDefault();
    try {
      await openImportDocument(href || importDocId, navigate);
    } catch (err: unknown) {
      showError(formatModeratorApiKeyError(err));
    }
  };

  const topicTagsForDisplay = useMemo(() => {
    const rawIds = rawArticle?.topic_ids ?? [];
    const rawNames = dedupeLabels(rawArticle?.topic_names ?? []);
    if (rawIds.length > 0 && rawNames.length > 0) {
      const n = Math.min(rawIds.length, rawNames.length);
      return Array.from({ length: n }, (_, i) => ({
        id: rawIds[i],
        name: rawNames[i],
      }));
    }

    const analysisIds = (analysis?.topicIds ?? '')
      .split(',')
      .map((s) => Number.parseInt(s.trim(), 10))
      .filter((id) => Number.isFinite(id) && id > 0);
    const analysisNames = dedupeLabels(analysis?.topic_names ?? []);
    if (analysisIds.length > 0 && analysisNames.length > 0) {
      const n = Math.min(analysisIds.length, analysisNames.length);
      return Array.from({ length: n }, (_, i) => ({
        id: analysisIds[i],
        name: analysisNames[i],
      }));
    }

    if (rawArticle?.topic) {
      return rawIds[0] != null
        ? [{ id: rawIds[0], name: rawArticle.topic }]
        : [];
    }
    return [];
  }, [rawArticle, analysis]);

  const buzzwords = dedupeLabels(analysis?.buzzword_names ?? []);
  const companies = parseCompanyField(analysis?.company);
  const sanitizedSummary = sanitizeSummary(analysis?.summary);
  const summaryParts = summaryParagraphs(sanitizedSummary);

  if (!state?.articleId || Number.isNaN(articleIdNum)) {
    return (
      <div className="article-analysis-page">
        <main className="aa-main">
          <div className="aa-card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ margin: '0 0 16px', color: 'var(--aa-muted)' }}>{t('articleAnalysis.noArticle')}</p>
            <Link to="/collected-raw" className="aa-btn aa-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              {t('articleAnalysis.backToArticles')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="article-analysis-page">
        <main className="aa-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <Loader2 className="animate-spin text-blue-600" size={36} aria-label={t('common.loading')} />
        </main>
      </div>
    );
  }

  if (fetchError === 'network') {
    return (
      <div className="article-analysis-page">
        <main className="aa-main">
          <div className="aa-card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ margin: '0 0 16px', color: 'var(--aa-muted)' }}>{t('articleAnalysis.networkError')}</p>
            <Link to="/collected-raw" className="aa-btn aa-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              {t('articleAnalysis.backToArticles')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (fetchError === 'not-found' || !rawArticle) {
    return (
      <div className="article-analysis-page">
        <main className="aa-main">
          <div className="aa-card" style={{ padding: 32, textAlign: 'center' }}>
            <p style={{ margin: '0 0 16px', color: 'var(--aa-muted)' }}>
              {t('articleAnalysis.notFound')}
            </p>
            <Link to="/collected-raw" className="aa-btn aa-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              {t('articleAnalysis.backToArticles')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const tagCount = topicTagsForDisplay.length + buzzwords.length;
  const hasTagContent = tagCount > 0;
  const hasCompanyContent = companies.length > 0;
  const hasSummaryContent = summaryParts.length > 0;

  const handleDataroomSetting = async () => {
    const articleId = articleIdNum;
    if (!Number.isFinite(articleId) || !rawArticle) return;

    if (!getModeratorApiKey()) {
      showError(t('dataPool.toastApiKey'));
      navigate('/dataroom-setting');
      return;
    }

    try {
      await dataroomApi.addArticle(articleId);
      addArticleToDataroom({
        articleId,
        title: rawArticle.title || t('common.articleNumber', { id: articleId }),
        url: href,
        summary: sanitizedSummary,
        topics: dedupeStrings([...topicTagsForDisplay.map((t) => t.name), ...buzzwords]),
        companies: dedupeStrings(companies),
        addedAt: new Date().toISOString(),
      });
      showSuccess(t('dataPool.toastAdded'));
    } catch (err: unknown) {
      const detail = formatModeratorApiKeyError(err);
      if (detail.toLowerCase().includes('has not been analyzed')) {
        showError(t('dataPool.toastNeedAnalysis'));
      } else if (detail.toLowerCase().includes('article not found')) {
        showError(t('dataPool.toastNotFound'));
      } else {
        showError(detail);
      }
    }

    navigate('/dataroom-setting');
  };

  const handleAddTopic = async (topicId: number) => {
    const attachedIds = new Set(rawArticle?.topic_ids ?? []);
    if (attachedIds.has(topicId)) {
      showError(t('articleAnalysis.tagDuplicate'));
      return;
    }
    if (!getModeratorApiKey()) {
      showError(t('dataPool.toastApiKey'));
      return;
    }

    setSavingTag(true);
    try {
      const res = await dataSourcesApi.addTopic(articleIdNum, { topic_id: topicId });
      applyTopicUpdate(res.data as RawDataWithTopicsRow);
      setAddingTag(false);
      showSuccess(t('articleAnalysis.tagAdded'));
    } catch (err: unknown) {
      showError(formatModeratorApiKeyError(err));
    } finally {
      setSavingTag(false);
    }
  };

  const cancelAddTag = () => {
    setAddingTag(false);
    setCatalogTopics([]);
  };

  const applyTopicUpdate = (updated: RawDataWithTopicsRow) => {
    setRawArticle((prev) =>
      prev
        ? {
            ...prev,
            topic_ids: updated.topic_ids ?? [],
            topic_names: updated.topic_names ?? [],
            topic: updated.topic ?? null,
          }
        : prev,
    );
    setAnalysis((prev) =>
      prev
        ? {
            ...prev,
            topic_names: updated.topic_names ?? [],
            topicIds: (updated.topic_ids ?? []).join(','),
          }
        : prev,
    );
  };

  const handleRemoveTopic = async (topicId: number) => {
    if (!getModeratorApiKey()) {
      showError(t('dataPool.toastApiKey'));
      return;
    }
    setRemovingTopicId(topicId);
    try {
      const res = await dataSourcesApi.removeTopic(articleIdNum, topicId);
      applyTopicUpdate(res.data as RawDataWithTopicsRow);
      showSuccess(t('articleAnalysis.tagRemoved'));
    } catch (err: unknown) {
      showError(formatModeratorApiKeyError(err));
    } finally {
      setRemovingTopicId(null);
    }
  };

  const handleAddCompany = async () => {
    const label = companyDraft.trim();
    if (!label) {
      showError(t('articleAnalysis.companyEmpty'));
      return;
    }
    if (companies.some((name) => name.toLowerCase() === label.toLowerCase())) {
      showError(t('articleAnalysis.companyDuplicate'));
      return;
    }
    if (!analysis) {
      showError(t('dataPool.toastNeedAnalysis'));
      return;
    }
    if (!getModeratorApiKey()) {
      showError(t('dataPool.toastApiKey'));
      return;
    }

    setSavingCompany(true);
    try {
      const res = await dataSourcesApi.addCompany(articleIdNum, { company: label });
      const updated = res.data as RawDataWithTopicsRow & { company_names?: string[] };
      const companyNames = updated.company_names ?? [];
      setAnalysis((prev) =>
        prev
          ? {
              ...prev,
              company: companyNames.length > 0 ? companyNames.join(', ') : prev.company,
            }
          : prev,
      );
      setCompanyDraft('');
      setAddingCompany(false);
      showSuccess(t('articleAnalysis.companyAdded'));
    } catch (err: unknown) {
      const detail = formatModeratorApiKeyError(err);
      if (detail.toLowerCase().includes('has not been analyzed')) {
        showError(t('dataPool.toastNeedAnalysis'));
      } else {
        showError(detail);
      }
    } finally {
      setSavingCompany(false);
    }
  };

  const cancelAddCompany = () => {
    setAddingCompany(false);
    setCompanyDraft('');
  };

  return (
    <div className="article-analysis-page">
      <main className="aa-main">
        <div className="aa-topbar">
          <div className="aa-crumbs">
            <Link to="/collected-raw" className="aa-crumbs-home" aria-label={t('articleAnalysis.articlesHome')}>
              <Home size={14} />
            </Link>
            <Link to="/collected-raw">{t('articleAnalysis.articles')}</Link>
            <span className="sep">›</span>
            <span>{t('articleAnalysis.title')}</span>
          </div>
          <div className="aa-actions">
            <button
              type="button"
              className="aa-btn aa-btn-ghost aa-btn-icon"
              title={t('articleAnalysis.dataroomSetting')}
              aria-label={t('articleAnalysis.dataroomSetting')}
              onClick={() => void handleDataroomSetting()}
            >
              <Settings size={14} />
            </button>
            <button
              type="button"
              className="aa-btn aa-btn-primary"
              onClick={() => showToast(t('articleAnalysis.newAnalysisSoon'))}
            >
              <Plus size={14} strokeWidth={2} aria-hidden />
              {t('articleAnalysis.newAnalysis')}
            </button>
          </div>
        </div>

        <div className="aa-head">
          <div>
            <h1 className="aa-h1">{t('articleAnalysis.title')}</h1>
            <div className="aa-sub">{t('articleAnalysis.subtitle')}</div>
            <div className="aa-pill-row">
              <span className="aa-pill">
                <span className="dot-g" />
                {t('articleAnalysis.indexed')}
              </span>
              <span className="aa-pill aa-pill-muted">{formatTimeAgo(rawArticle.scraped_at ?? undefined, t)}</span>
            </div>
          </div>
        </div>

        <div className="aa-body-grid">
          <section className="aa-card aa-side-card">
            <div className="aa-card-head">
              <div className="aa-card-head-left">
                <span className="aa-card-icon aa-card-icon--tags" aria-hidden>
                  <Tag size={14} />
                </span>
                <h3 className="aa-card-title">{t('articleAnalysis.tags')}</h3>
              </div>
              <span className="aa-count-badge">{tagCount}</span>
            </div>

            <div className="aa-card-body">
              {!hasTagContent ? (
                <div className="aa-empty-state">
                  <p className="aa-empty-title">{t('articleAnalysis.noAnalysisYet')}</p>
                  <p className="aa-empty-desc">{t('articleAnalysis.noTags')}</p>
                </div>
              ) : (
                <>
                  <div className="aa-tag-list">
                    {topicTagsForDisplay.map((tag, i) => (
                      <div key={`${tag.id}-${tag.name}`} className={`aa-tag ${toneForIndex(i)}`}>
                        <span className="swatch" />
                        <span className="aa-tag-label">{tag.name}</span>
                        <button
                          type="button"
                          className="aa-tag-remove"
                          title={t('articleAnalysis.removeTag')}
                          aria-label={t('articleAnalysis.removeTag')}
                          disabled={removingTopicId === tag.id || savingTag}
                          onClick={() => {
                            void handleRemoveTopic(tag.id);
                          }}
                        >
                          {removingTopicId === tag.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <X size={12} strokeWidth={2.5} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  {buzzwords.length > 0 ? (
                    <>
                      <div className="aa-divider" />
                      <p className="aa-subsection-label">{t('articleAnalysis.buzzwords')}</p>
                      <div className="aa-tag-list">
                        {buzzwords.map((label, i) => (
                          <div key={`${label}-buzz-${i}`} className={`aa-tag ${toneForIndex(i + 2)}`}>
                            <span className="swatch" />
                            {label}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </div>

            <div className="aa-card-footer">
              {addingTag ? (
                <TopicSearchPicker
                  options={catalogTopics}
                  excludedIds={rawArticle.topic_ids ?? []}
                  loading={loadingTopics}
                  disabled={savingTag}
                  placeholder={t('articleAnalysis.tagPlaceholder')}
                  dropdownUp
                  onSelect={(topicId) => {
                    void handleAddTopic(topicId);
                  }}
                  onCancel={cancelAddTag}
                />
              ) : (
                <button
                  type="button"
                  className="aa-round-btn"
                  title={t('articleAnalysis.addTag')}
                  aria-label={t('articleAnalysis.addTag')}
                  onClick={() => setAddingTag(true)}
                >
                  <Plus size={14} strokeWidth={2} />
                </button>
              )}
            </div>
          </section>

          <section className="aa-center">
            <div className="aa-card aa-header-card">
              <div className="aa-meta-row">
                <span className="aa-pill aa-pill-meta">
                  <Calendar size={12} />
                  {formatMetaDateTime(rawArticle.scraped_at ?? undefined, localeTag)}
                </span>
                <span className="aa-pill aa-pill-id">{t('common.articleNumber', { id: rawArticle.id })}</span>
              </div>

              <h2 className="aa-article-h">{rawArticle.title}</h2>
              {href ? (
                <a
                  className="aa-article-url"
                  href={importDocId != null ? '#' : href}
                  target={importDocId != null ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  onClick={handleArticleLinkClick}
                >
                  <Link2 size={13} className="aa-article-url-icon" />
                  <span className="aa-article-url-text">
                    {linkDisplayLabel(href, rawArticle.title)}
                  </span>
                  <ExternalLink size={13} className="aa-article-url-external" />
                </a>
              ) : (
                <span className="aa-muted-text">{t('articleAnalysis.noUrl')}</span>
              )}

              <div className="aa-divider" />

              <div className="aa-section-head">
                <span className="aa-card-icon aa-card-icon--summary" aria-hidden>
                  <FileText size={14} />
                </span>
                <h3 className="aa-section-title">{t('articleAnalysis.summary')}</h3>
              </div>
              {!analysis || !hasSummaryContent ? (
                <p className="aa-muted-text">{t('articleAnalysis.noSummary')}</p>
              ) : (
                <div className="aa-summary-text">
                  {summaryParts.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="aa-card aa-side-card">
            <div className="aa-card-head">
              <div className="aa-card-head-left">
                <span className="aa-card-icon aa-card-icon--companies" aria-hidden>
                  <Building2 size={14} />
                </span>
                <h3 className="aa-card-title">{t('articleAnalysis.companies')}</h3>
              </div>
              <span className="aa-count-badge">{companies.length}</span>
            </div>

            <div className="aa-card-body">
              {!hasCompanyContent ? (
                <div className="aa-empty-state">
                  <p className="aa-empty-title">{t('articleAnalysis.noAnalysisYet')}</p>
                  <p className="aa-empty-desc">{t('articleAnalysis.noCompanies')}</p>
                </div>
              ) : (
                <div className="aa-co-list">
                  {companies.map((name, i) => (
                    <div key={`${name}-${i}`} className="aa-co">
                      <div className="aa-co-logo" style={{ background: CO_LOGO_BG[i % CO_LOGO_BG.length] }}>
                        {initialsFromName(name)}
                      </div>
                      <div>
                        <div className="name">{name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="aa-card-footer">
              {addingCompany ? (
                <form
                  className="aa-tag-add"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleAddCompany();
                  }}
                >
                  <input
                    ref={companyInputRef}
                    type="text"
                    className="aa-tag-add-input"
                    value={companyDraft}
                    onChange={(e) => setCompanyDraft(e.target.value)}
                    placeholder={t('articleAnalysis.companyPlaceholder')}
                    disabled={savingCompany}
                    aria-label={t('articleAnalysis.companyPlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelAddCompany();
                    }}
                  />
                  <button
                    type="submit"
                    className="aa-round-btn aa-round-btn--primary"
                    title={t('articleAnalysis.addCompany')}
                    aria-label={t('articleAnalysis.addCompany')}
                    disabled={savingCompany || !companyDraft.trim()}
                  >
                    {savingCompany ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={2} />}
                  </button>
                  <button
                    type="button"
                    className="aa-round-btn"
                    title={t('common.cancel')}
                    aria-label={t('common.cancel')}
                    onClick={cancelAddCompany}
                    disabled={savingCompany}
                  >
                    ×
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  className="aa-round-btn"
                  title={t('articleAnalysis.addCompany')}
                  aria-label={t('articleAnalysis.addCompany')}
                  onClick={() => setAddingCompany(true)}
                >
                  <Plus size={14} strokeWidth={2} />
                </button>
              )}
            </div>
          </section>
        </div>
        {toast ? (
          <div className="aa-toast" role="status" aria-live="polite">
            {toast}
          </div>
        ) : null}
      </main>
    </div>
  );
}
